import React, { useEffect, useMemo, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { useSidebarStore } from '../../../store/sidebar.store';
import { useHeaderStore } from '../../../store/header.store';
import { useLocation, useNavigate } from 'react-router-dom';
import AppGetIcon from '../AppGetIcon';
import { ItemDto } from '../../dtos/initData.dto';
import './sideBar.css';
import appLogo from '../../../assets/images/logo_Tiglobal.png';
import { useTheme } from '../../../contexts/ThemeContext';
import { lightTheme } from '../../theme/types/light-theme.type';
import { darkTheme } from '../../theme/types/dark-theme.type';
import { useAuth } from '../../../contexts/AuthContext';

interface SidebarProps {
  itemsSideBar: ItemDto[];
}

// Limpia posibles barras al final de la ruta
const cleanRoute = (r?: string) => (r ?? '').trim().replace(/\/+$/, '');

export default function SideBar({ itemsSideBar }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [loading, setLoading] = useState(false);

  // Tooltip collapsed
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [hoverTop, setHoverTop] = useState(0);
  const navRefs = useRef<{ [key: number]: HTMLLIElement | null }>({});

  const headerStore = useHeaderStore(s => ({ setModule: s.setModule }), shallow);
  const sidebarStorage = useSidebarStore(s => ({
    sidebarClose: s.sidebarClose,
    navSelected: s.navSelected,
    active: s.active,
    updatesidebarClose: s.updatesidebarClose,
    updatenavSelected: s.updatenavSelected,
    updateactive: s.updateactive,
  }), shallow);

  // Normalizar rutas
  const normItems = useMemo(
    () => itemsSideBar.map(i => ({ ...i, route: cleanRoute(i.route) })),
    [itemsSideBar]
  );

  // Definición de secciones y agrupamiento
  const sectionDefs = useMemo(() => [
    { key: 'adminUsers', title: 'Administración de usuarios', sectionOrder: 1 },
    { key: 'report',     title: 'Reportes',                   sectionOrder: 2 },
    { key: 'admin',      title: 'Home',                       sectionOrder: 0 },
  ], []);
  const defaultGroup = 'Otros';
  const sortByOrder = (a: ItemDto, b: ItemDto) => (a.order ?? 1e9) - (b.order ?? 1e9);

  const groupedItems = useMemo(() => {
    const groups: Record<string, ItemDto[]> = {};
    sectionDefs.forEach(sec => groups[sec.title] = []);
    groups[defaultGroup] = [];

    normItems.filter(i => i.showMenu === 1).forEach(item => {
      const sec = sectionDefs.find(s => item.route?.includes(s.key));
      (sec ? groups[sec.title] : groups[defaultGroup]).push(item);
    });
    Object.values(groups).forEach(arr => arr.sort(sortByOrder));
    return groups;
  }, [normItems, sectionDefs]);

  const orderedSectionTitles = useMemo(() => {
    const explicit = sectionDefs
      .slice().sort((a,b) => a.sectionOrder - b.sectionOrder)
      .map(s => s.title);
    if (groupedItems[defaultGroup]?.length) explicit.push(defaultGroup);
    return explicit.filter(t => groupedItems[t]?.length);
  }, [groupedItems, sectionDefs]);

  // Cada vez que cambia la URL, marcar navSelected
  useEffect(() => {
    const current = cleanRoute(location.pathname);
    let winner: ItemDto|undefined;
    let longest = -1;
    normItems.forEach(item => {
      const r = cleanRoute(item.route);
      if (r && (current === r || current.startsWith(`${r}/`)) && r.length > longest) {
        winner = item;
        longest = r.length;
      }
    });
    if (winner && sidebarStorage.navSelected !== winner.id) {
      sidebarStorage.updatenavSelected(winner.id);
      headerStore.setModule(winner.name, winner.route ?? '');
    }
  }, [location.pathname]);

  const isSelected = (item: ItemDto) => {
    const p = cleanRoute(location.pathname);
    const r = cleanRoute(item.route);
    return sidebarStorage.navSelected === item.id || p === r || p.startsWith(`${r}/`);
  };

  // Tooltip collapsed
  const handleMouseEnter = (item: ItemDto) => {
    setHoveredId(item.id);
    const rect = navRefs.current[item.id]?.getBoundingClientRect();
    if (rect) setHoverTop(rect.top + rect.height / 2 - 20);
  };
  const handleMouseLeave = () => setHoveredId(null);

  // Toggle tema
  const handleToggleTheme = () =>
    setTheme(theme.name === 'dark' ? lightTheme : darkTheme);

  // Logout
  const signOut = async () => {
    setLoading(true);
    localStorage.clear();
    logout();
    setLoading(false);
  };

  // ** NUEVO: click handler centralizado **
  const handleNavClick = (item: ItemDto) => {
    // marcar seleccionado
    if (sidebarStorage.navSelected !== item.id) {
      sidebarStorage.updatenavSelected(item.id);
    }

    // si está abierto y NO tiene hijos, limpiamos active (quita overlay)
    if (!sidebarStorage.sidebarClose) {
      if (item.children && item.children.length > 0) {
        sidebarStorage.updateactive(item.id);
      } else {
        sidebarStorage.updateactive(null);
      }
    }

    // setear módulo y navegar
    headerStore.setModule(item.name, item.route ?? '');
    navigate(item.route ?? '');
  };

  return (
    <>
      {/* overlay solo si hay active y sidebar abierto */}
      {sidebarStorage.active && !sidebarStorage.sidebarClose && (
        <div className="overlay" onClick={() => sidebarStorage.updateactive(null)} />
      )}

      <div className={`sidebar ${sidebarStorage.sidebarClose ? 'sidebar--close' : ''}`}>
        {/* toggle */}
        <div className={`buttons ${sidebarStorage.sidebarClose ? 'buttons--close' : ''}`}>
          <button
            className="button"
            onClick={() => {
              sidebarStorage.updatesidebarClose(!sidebarStorage.sidebarClose);
              sidebarStorage.updateactive(null);
            }}
          >
            <AppGetIcon
              name="arrow-back"
              strokeColor="var(--color-on-surface-variant-main)"
              classIcon="size-icon-sidebar-button"
            />
          </button>
        </div>

        {/* logo / nombre */}
        <div className={`institution-header ${sidebarStorage.sidebarClose ? 'institution-header--close' : ''}`}>
          <div className="institution-logo">
            <img src={appLogo} alt="App Logo" className="logo-img" />
          </div>
        </div>

        <div className="sidebar-separator" />

        {/* menú */}
        <ul className="nav-links">
          {orderedSectionTitles.map(sectionTitle => {
            const items = groupedItems[sectionTitle];
            return !items?.length ? null : (
              <React.Fragment key={sectionTitle}>
                {!sidebarStorage.sidebarClose && sectionTitle !== defaultGroup && (
                  <li className="sidebar-section-title">{sectionTitle}</li>
                )}
                {items.map(item => (
                  <li
                    key={item.id}
                    ref={el => navRefs.current[item.id] = el}
                    className="nav-sub-menu-li"
                    onMouseEnter={() => handleMouseEnter(item)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleNavClick(item)}
                  >
                    {/* tooltip collapsed */}
                    {sidebarStorage.sidebarClose && hoveredId === item.id && (
                      <div
                        className="nav-hover"
                        style={{ top: hoverTop }}
                      >
                        {item.name}
                      </div>
                    )}

                    <div className={`nav-item ${isSelected(item) ? 'nav--selected' : ''}`}>
                      <div className="item-container-name">
                        <AppGetIcon
                          name={item.icon || ''}
                          classIcon="size-icon-sidebar"
                          fillColor={isSelected(item) ? '#FFFFFF' : '#5B5F62'}
                        />
                        <div className="item-name">{item.name}</div>
                      </div>
                    </div>
                  </li>
                ))}
              </React.Fragment>
            );
          })}
        </ul>

        {/* footer */}
        {/* <div className={`sidebar-footer ${sidebarStorage.sidebarClose ? 'sidebar-footer--close' : ''}`}>
          <div className="footer-item" onClick={handleToggleTheme}>
            <AppGetIcon
              name={theme.name === 'dark' ? 'sun' : 'moon'}
              classIcon="size-icon-sidebar"
              fillColor="var(--color-on-surface-variant-main)"
            />
            {!sidebarStorage.sidebarClose && (
              <span>{theme.name === 'dark' ? 'Modo claro' : 'Modo oscuro'}</span>
            )}
          </div>
        </div> */}
      </div>
    </>
  );
}
